import { useEffect, useState } from "react";
import api from "@/lib/api";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line, CartesianGrid, Cell,
} from "recharts";
import { TrendingUp, Users, Trophy, Ban, IndianRupee } from "lucide-react";

const AXIS = { fontSize: 11, fontFamily: "IBM Plex Mono" };

function Card({ title, children, tid }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-xl p-5 hard-shadow-sm" data-testid={tid}>
      <h3 className="font-head font-bold mb-4">{title}</h3>
      {children}
    </div>
  );
}

function KPI({ icon: Icon, label, value }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-xl p-4 flex items-center gap-3 hard-shadow-sm">
      <div className="h-10 w-10 rounded-lg bg-klein/10 text-klein grid place-items-center"><Icon className="h-5 w-5" /></div>
      <div>
        <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">{label}</div>
        <div className="font-head text-xl font-black">{value}</div>
      </div>
    </div>
  );
}

export default function Analytics() {
  const [d, setD] = useState(null);
  const [lg, setLg] = useState(null);
  useEffect(() => { api.get("/analytics/dashboard").then((r) => setD(r.data)); }, []);
  useEffect(() => { api.get("/analytics/leadgen-daily?days=14").then((r) => setLg(r.data)).catch(() => {}); }, []);
  if (!d) return <div className="p-6 font-mono text-sm">Loading analytics…</div>;

  const LG_COLORS = ["#002FA7", "#FFD700", "#DC2626", "#059669", "#7C3AED", "#EA580C"];
  const t = d.totals;
  return (
    <div className="p-6 max-w-6xl">
      <h1 className="font-head text-2xl font-extrabold tracking-tight mb-1">Analytics</h1>
      <p className="text-xs text-zinc-500 font-mono mb-5">Funnel · tier conversion · ghost points · objections · trend · ICP drift</p>

      <div className="grid md:grid-cols-4 gap-3 mb-5">
        <KPI icon={Users} label="Total leads" value={t.leads} />
        <KPI icon={Trophy} label="Won" value={`${t.won} (${t.win_rate}%)`} />
        <KPI icon={Ban} label="Lost" value={t.lost} />
        <KPI icon={IndianRupee} label="Revenue" value={`₹${(t.revenue || 0).toLocaleString()}`} />
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        {lg && lg.reps.length > 0 && (
          <Card title="Leads generated per day by lead-gen rep" tid="chart-leadgen-daily">
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={lg.series}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
                <XAxis dataKey="date" tick={{ fontSize: 9, fontFamily: "IBM Plex Mono" }} tickFormatter={(v) => v.slice(5)} />
                <YAxis tick={AXIS} allowDecimals={false} />
                <Tooltip />
                {lg.reps.map((r, i) => (
                  <Bar key={r} dataKey={r} stackId="lg" fill={LG_COLORS[i % LG_COLORS.length]} />
                ))}
              </BarChart>
            </ResponsiveContainer>
            <p className="text-[10px] text-zinc-400 mt-1">Daily leads each lead-gen rep added to the CRM over the last 14 days.</p>
          </Card>
        )}

        <Card title="Pipeline funnel" tid="chart-funnel">
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={d.funnel} layout="vertical" margin={{ left: 20 }}>
              <XAxis type="number" tick={AXIS} />
              <YAxis type="category" dataKey="stage" width={95} tick={AXIS} />
              <Tooltip />
              <Bar dataKey="count" fill="#002FA7" />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card title="Conversion rate by tier" tid="chart-tier">
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={d.conversion_by_tier}>
              <XAxis dataKey="tier" tick={{ fontSize: 9, fontFamily: "IBM Plex Mono" }} interval={0} />
              <YAxis tick={AXIS} unit="%" />
              <Tooltip />
              <Bar dataKey="rate" fill="#FFD700" stroke="#000">
                {d.conversion_by_tier.map((e, i) => <Cell key={i} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card title="Ghost-point breakdown" tid="chart-ghost">
          {d.ghost_breakdown.length ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={d.ghost_breakdown}>
                <XAxis dataKey="point" tick={{ fontSize: 9, fontFamily: "IBM Plex Mono" }} interval={0} />
                <YAxis tick={AXIS} /><Tooltip />
                <Bar dataKey="count" fill="#DC2626" />
              </BarChart>
            </ResponsiveContainer>
          ) : <p className="text-xs text-zinc-400">No ghosted leads.</p>}
        </Card>

        <Card title="Objection frequency & recovery" tid="chart-objection">
          {d.objection_breakdown.length ? (
            <div className="space-y-3">
              {d.objection_breakdown.map((o) => (
                <div key={o.objection}>
                  <div className="flex justify-between text-xs mb-1"><span className="font-medium">{o.objection}</span><span className="font-mono text-zinc-500">{o.count} logged · {o.recovery_rate}% recovered</span></div>
                  <div className="h-3 bg-zinc-200 flex">
                    <div className="h-3 bg-red-500" style={{ width: `${100 - o.recovery_rate}%` }} />
                    <div className="h-3 bg-emerald-500" style={{ width: `${o.recovery_rate}%` }} />
                  </div>
                </div>
              ))}
            </div>
          ) : <p className="text-xs text-zinc-400">No objections logged.</p>}
        </Card>

        <Card title="Monthly won-client trend" tid="chart-trend">
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={d.monthly_trend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
              <XAxis dataKey="month" tick={{ fontSize: 9, fontFamily: "IBM Plex Mono" }} />
              <YAxis tick={AXIS} allowDecimals={false} /><Tooltip />
              <Line type="monotone" dataKey="won" stroke="#002FA7" strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
          <p className="text-[10px] text-zinc-400 mt-1 flex items-center gap-1"><TrendingUp className="h-3 w-3" /> Watch for dips — catch them early.</p>
        </Card>

        <Card title="ICP drift over time" tid="chart-drift">
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={d.icp_drift}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
              <XAxis dataKey="date" tick={{ fontSize: 9, fontFamily: "IBM Plex Mono" }} />
              <YAxis tick={AXIS} /><Tooltip />
              <Line type="monotone" dataKey="experience_median" stroke="#DC2626" strokeWidth={2} name="Exp median (yrs)" />
            </LineChart>
          </ResponsiveContainer>
        </Card>
      </div>
    </div>
  );
}
